import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { consulta, ErrorDeLaApi, fetchConSesion, mensajePorEstado, pedir } from './nucleo'
import { hayQueReducir, reducirSiHaceFalta } from './reducir'
import type { Lista, UsoDeUnMedio } from './tipos'

/**
 * La biblioteca de medios de una web. Subir es la única petición del panel que no es
 * JSON: va en `multipart/form-data`, con el fichero en `file` y el resto de campos en
 * `_payload`, que es como lo espera Payload. El `alt` es obligatorio (§8): sin él no
 * hay accesibilidad ni SEO, y el servidor lo rechaza igual.
 */
export type Medio = {
  id: number
  alt: string
  titulo?: null | string
  /** Su texto alternativo y su título en los otros idiomas de la web (§37). */
  traducciones?: null | Partial<Record<'en' | 'es' | 'pt', Record<string, string>>>
  filename?: string
  mimeType?: string
  filesize?: number
  width?: number | null
  height?: number | null
  url?: string
  focalX?: number | null
  focalY?: number | null
  sizes?: Record<string, { url?: null | string; width?: null | number; height?: null | number }>
  updatedAt?: string
  createdAt?: string
  /** Biblioteca v2 (plan del CMS dinámico §10): dónde vive, en qué punto está y qué es de verdad. */
  visibilidad?: 'privado' | 'publico'
  estado?: 'analizando' | 'bloqueado' | 'fallido' | 'listo' | 'pendiente' | 'procesando' | 'subiendo'
  error?: null | string
  tipoReal?: null | string
  nombreOriginal?: null | string
  ancho?: null | number
  alto?: null | number
  paginas?: null | number
  carpeta?: null | string
  usos?: null | number
  lote?: null | number
  analisis?: { firma?: null | string; resultado?: null | string; fecha?: null | string } | null
}

/** De qué familia es un medio, por su tipo de verdad (o el de Payload, en lo de antes). */
export type FamiliaDeMedio = 'documento' | 'imagen' | 'svg' | 'otro'
const IMAGENES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif', 'image/gif']
export const familiaDe = (m: Pick<Medio, 'mimeType' | 'tipoReal'>): FamiliaDeMedio => {
  const tipo = m.tipoReal || m.mimeType || ''
  if (IMAGENES.includes(tipo)) return 'imagen'
  if (tipo === 'image/svg+xml') return 'svg'
  return tipo ? 'documento' : 'otro'
}

/** Los filtros de la biblioteca, tal como van en la dirección de la pantalla. */
export type FiltrosDeMedios = {
  tipo?: '' | 'documentos' | 'imagenes' | 'svg'
  visibilidad?: '' | 'privado' | 'publico'
  estado?: '' | 'bloqueado' | 'en-curso' | 'fallido' | 'listo'
  lote?: string
  carpeta?: string
  /** Solo lo que se puede elegir en un campo (el selector de un editor). */
  soloListos?: boolean
}

const TIPOS_DE_FILTRO: Record<string, string[]> = {
  imagenes: IMAGENES,
  svg: ['image/svg+xml'],
}

/** Los filtros, como condiciones `where[and][n]…` de la API de Payload. */
export const condicionesDe = (f: FiltrosDeMedios): Record<string, string> => {
  const c: Record<string, string> = {}
  let n = 0
  const y = (sufijo: string, valor: string) => {
    c[`where[and][${n}]${sufijo}`] = valor
    n += 1
  }
  if (f.tipo === 'imagenes' || f.tipo === 'svg') {
    const lista = TIPOS_DE_FILTRO[f.tipo]!.join(',')
    c[`where[and][${n}][or][0][tipoReal][in]`] = lista
    c[`where[and][${n}][or][1][mimeType][in]`] = lista
    n += 1
  } else if (f.tipo === 'documentos') {
    const lista = [...IMAGENES, 'image/svg+xml'].join(',')
    y('[tipoReal][not_in]', lista)
    y('[tipoReal][exists]', 'true')
  }
  if (f.visibilidad) y('[visibilidad][equals]', f.visibilidad)
  if (f.soloListos) y('[estado][equals]', 'listo')
  else if (f.estado === 'en-curso') y('[estado][in]', 'pendiente,subiendo,analizando,procesando')
  else if (f.estado) y('[estado][equals]', f.estado)
  if (f.lote && /^\d+$/.test(f.lote)) y('[lote][equals]', f.lote)
  if (f.carpeta) y('[carpeta][equals]', f.carpeta)
  return c
}

export type Uso = { cliente: number; usadoBytes: number; cuotaBytes: number; libreBytes: number; maximoPorFicheroBytes: number }

export const miniaturaDe = (m: Medio | undefined): null | string => m?.sizes?.miniatura?.url || m?.url || null
export const tarjetaDe = (m: Medio | undefined): null | string => m?.sizes?.tarjeta?.url || m?.url || null

export const useMedios = (sitio: number, busqueda = '', pagina = 1, porPagina = 40, filtros: FiltrosDeMedios = {}) =>
  useQuery({
    queryKey: ['medios', sitio, busqueda, pagina, porPagina, filtros],
    // Mientras haya algo subiendo o analizándose, se vuelve a preguntar: el estado lo cambia la cola del núcleo.
    refetchInterval: (q) => ((q.state.data?.docs ?? []).some((m) => m.estado && m.estado !== 'listo' && m.estado !== 'fallido' && m.estado !== 'bloqueado') ? 3000 : false),
    queryFn: () =>
      pedir<Lista<Medio>>(
        `/medios${consulta({
          'where[sitio][equals]': sitio,
          ...(busqueda ? { 'where[or][0][alt][contains]': busqueda, 'where[or][1][filename][contains]': busqueda, 'where[or][2][nombreOriginal][contains]': busqueda } : {}),
          ...condicionesDe(filtros),
          limit: porPagina,
          page: pagina,
          sort: '-updatedAt',
          depth: 0,
        })}`,
      ),
  })

export const useMedio = (id: null | number) =>
  useQuery({
    queryKey: ['medio', id],
    enabled: id !== null,
    queryFn: () => pedir<Medio>(`/medios/${id}${consulta({ depth: 0 })}`),
  })

export const useUsoDeMedios = (espacio: number) =>
  useQuery({ queryKey: ['medios-uso', espacio], queryFn: () => pedir<Uso>(`/medios/uso${consulta({ cliente: espacio })}`) })

/**
 * El máximo por fichero del plan del espacio, para reducir antes de subir. Sin el dato
 * (un fallo de red, un rol que no lo ve) no se reduce nada: el núcleo sigue siendo la
 * puerta y dirá lo suyo.
 */
export const maximoPorFicheroDe = async (espacio: number): Promise<null | number> => {
  try {
    return (await pedir<Uso>(`/medios/uso${consulta({ cliente: espacio })}`)).maximoPorFicheroBytes
  } catch {
    return null
  }
}

/**
 * Subir a la biblioteca. Una imagen que pasa del máximo por fichero del plan se reduce
 * antes en el navegador (api/reducir.ts); mientras tanto, `reduciendo` lleva su nombre
 * para que la pantalla lo diga.
 */
export const useSubirMedio = (sitio: number, espacio: number) => {
  const clienteDeConsultas = useQueryClient()
  const [reduciendo, setReduciendo] = useState<null | string>(null)
  const mutacion = useMutation({
    mutationFn: async ({ fichero: original, alt, titulo, maximoBytes }: { alt: string; fichero: File; maximoBytes?: null | number; titulo?: string }) => {
      let fichero = original
      if (hayQueReducir(original, maximoBytes)) {
        try {
          fichero = await reducirSiHaceFalta(original, maximoBytes, undefined, () => setReduciendo(original.name))
        } finally {
          setReduciendo(null)
        }
      }
      const cuerpo = new FormData()
      cuerpo.append('file', fichero, fichero.name)
      cuerpo.append('_payload', JSON.stringify({ alt, titulo: titulo || undefined, sitio, cliente: espacio }))
      const respuesta = await fetchConSesion('/api/medios', { method: 'POST', credentials: 'same-origin', body: cuerpo })
      const json = (await respuesta.json().catch(() => ({}))) as { doc?: Medio; errors?: Array<{ message?: string; data?: { errors?: Array<{ message?: string }> } }> }
      if (!respuesta.ok) {
        const primero = json.errors?.[0]
        throw new ErrorDeLaApi(primero?.data?.errors?.[0]?.message ?? primero?.message ?? `No se ha podido subir. ${mensajePorEstado(respuesta.status)}`, respuesta.status)
      }
      return json.doc as Medio
    },
    onSuccess: () => {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['medios', sitio] })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['medios-uso', espacio] })
    },
  })
  return { ...mutacion, reduciendo }
}

export const useGuardarMedio = (sitio: number) => {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: ({ id, datos }: { datos: Partial<Medio>; id: number }) => pedir<{ doc: Medio }>(`/medios/${id}`, { metodo: 'PATCH', cuerpo: datos }),
    onSuccess: (_r, { id }) => {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['medios', sitio] })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['medio', id] })
    },
  })
}

/**
 * Dónde se usa una imagen (`GET /medios/usos`): páginas, entradas, productos, ajustes… con si
 * están publicados. Borrarla la quita de todos esos sitios, así que se enseña antes.
 */
export const usosDe = (id: number) => pedir<{ id: number; usos: UsoDeUnMedio[]; publicados: number }>(`/medios/usos${consulta({ id })}`)

/**
 * Borrar. Lo que se ve en la web el núcleo no lo borra sin `confirmar=si` (responde 409 con
 * dónde se usa): la pantalla lo pide después de enseñar los usos y de que el cliente lo confirme.
 */
export const useBorrarMedio = (sitio: number, espacio: number) => {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => pedir(`/medios/${id}${consulta({ confirmar: 'si' })}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['medios', sitio] })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['medios-uso', espacio] })
    },
  })
}

/**
 * Cambiar la visibilidad de un medio (`POST /medios/visibilidad`): el núcleo lo mueve de bucket en
 * su cola y, al pasar a privado, dice en qué páginas publicadas se ve (dejará de verse ahí).
 */
export const useCambiarVisibilidad = (sitio: number) => {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: ({ id, visibilidad }: { id: number; visibilidad: 'privado' | 'publico' }) =>
      pedir<{ id: number; visibilidad: string; encolado: boolean; usosPublicados: UsoDeUnMedio[] }>('/medios/visibilidad', { metodo: 'POST', cuerpo: { id, visibilidad } }),
    onSuccess: (_r, { id }) => {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['medios', sitio] })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['medio', id] })
    },
  })
}

export const tamano = (bytes: null | number | undefined): string => {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/**
 * Crear una imagen con IA (CLAUDE.md §8): la hace el núcleo, la guarda en la biblioteca de la
 * web como una subida más y la cobra en créditos. Antes de gastar, el panel pide cuánto costaría.
 */
export type FormatoDeImagen = 'cuadrada' | 'horizontal' | 'vertical'
export type CalidadDeImagen = 'alta' | 'normal'
export type ImagenCreada = { medio: { id: number; url: null | string; alt: string }; creditos: { consumidos: number; saldo: number }; modelo: string }

export const useCosteDeImagen = (sitio: number, formato: FormatoDeImagen, calidad: CalidadDeImagen) =>
  useQuery({
    queryKey: ['coste-de-imagen', sitio, formato, calidad],
    queryFn: () => pedir<{ creditos: number; saldo: number }>('/medios/generar', { metodo: 'POST', cuerpo: { sitio, formato, calidad, estimar: true } }),
    enabled: sitio > 0,
    retry: false,
    // Al cambiar de formato se queda la cifra anterior mientras llega la nueva, sin parpadear.
    placeholderData: (anterior) => anterior,
  })

export const useCrearImagen = (sitio: number, espacio: number) => {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (datos: { prompt: string; formato: FormatoDeImagen; calidad: CalidadDeImagen }) => pedir<ImagenCreada>('/medios/generar', { metodo: 'POST', cuerpo: { sitio, ...datos } }),
    onSuccess: () => {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['medios', sitio] })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['medios-uso', espacio] })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['saldo', espacio] })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['coste-de-imagen', sitio] })
    },
  })
}
